import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import { Fragment, PropsWithChildren } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { NavLink } from 'react-router-dom';

import IconAlert from '@/assets/ic_alert_navbar.svg';
import useToast from '@/hooks/useToast';
import { PropsWithClassName, NavigationItem } from '@/types';
import { cn } from '@/utils';

type Props = PropsWithClassName<
  PropsWithChildren<{
    items: NavigationItem[];
    showTDAP?: boolean;
    showSubmenu?: boolean;
    isActive?: boolean;
    customClass?: string;
    isPremium?: boolean;
    buttonClassName?: string;
  }>
>;

const btnClass =
  'w-full flex items-center text-sm font-medium gap-2 py-4 px-4 text-gdt-gray hover:bg-gdt-gray-7 item  hover:rounded-xl';

const FlyoutMenu = ({
  children,
  items,
  className,
  buttonClassName,
  showSubmenu,
  showTDAP,
  isActive,
  customClass,
  isPremium,
}: Props) => {
  const toast = useToast();

  return (
    <Popover className={cn('relative', buttonClassName)}>
      {({ open, close }) => {
        return (
          <>
            <PopoverButton
              className={cn(
                'flex gap-1 text-sm font-semibold tracking-[0.2px] text-center text-white relative py-2 focus-visible:outline-none ',
                {
                  active: isActive,
                  'items-center': !isPremium,
                  'item--navbar': !showSubmenu,
                  'bg-gdt-gray-7 font-bold text-gdt-gray-3 rounded-xl':
                    showSubmenu && isActive,
                },
                buttonClassName
              )}
            >
              <span className={className}>{children}</span>
              <FiChevronDown
                aria-hidden="true"
                className={cn(
                  'text-white w-5 h-5 transition-all ease-in-out duration-100',
                  {
                    'rotate-180': open,
                    'text-gdt-gray-5': showSubmenu,
                    '-rotate-90': showSubmenu && open,
                  }
                )}
              />
            </PopoverButton>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <PopoverPanel
                className={cn(
                  `absolute left-1/2 z-10 ${
                    customClass || 'mt-2'
                  } flex w-screen max-w-min -translate-x-1/2 px-4`,
                  {
                    'left-auto right-[calc(-100%-47px)] -translate-x-0 -top-[8px]':
                      showSubmenu,
                  }
                )}
              >
                <div
                  className={cn(
                    'w-56 shrink rounded-xl bg-white shadow-[0px 4px 48px 0px #0000000F] divide-y divide-gray-[#F2F2F2]',
                    { 'w-[250px]': showSubmenu }
                  )}
                >
                  {items.map((item) => {
                    const className = cn(btnClass, {
                      'text-gdt-gray-3': item.isContrast,
                      '!border-none': item.disabled,
                      'font-bold': item.isActive,
                      'bg-gdt-gray-7': item.isActive,
                    });

                    if (item.onClick) {
                      return (
                        <button
                          key={item.name}
                          className={className}
                          type="button"
                          onClick={item.onClick}
                        >
                          {item.icon}
                          {item.name}
                        </button>
                      );
                    }

                    if (!item.children || (item.children && !showTDAP)) {
                      return (
                        <NavLink
                          key={item.name}
                          className={className}
                          to={!item.disabled ? item.href : ''}
                          onClick={() => {
                            item.disabled &&
                              toast.show(
                                'Muy pronto vas a poder competir en los Desafíos 1 a 1'
                              );
                            close();
                          }}
                        >
                          {item.icon}
                          {item.name}
                          {item.highlight && <img src={IconAlert} />}
                        </NavLink>
                      );
                    } else {
                      return (
                        showTDAP && (
                          <FlyoutMenu
                            key={`subSubMenu-item-flyout-${item.name}`}
                            buttonClassName="hover:bg-gdt-gray-7 hover:rounded-t-xl"
                            className={cn(className, 'py-2')}
                            isActive={item.isActive}
                            items={item.children}
                            showSubmenu={true}
                          >
                            {item.icon}
                            {item.name}
                          </FlyoutMenu>
                        )
                      );
                    }
                  })}
                </div>
              </PopoverPanel>
            </Transition>
          </>
        );
      }}
    </Popover>
  );
};

export default FlyoutMenu;
